import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import posthog from 'posthog-js';

const BLUE  = 'var(--signal)';
const GREEN = 'var(--go)';
const TEXT1 = 'var(--ink)';
const TEXT2 = 'var(--slate)';
const TEXT3 = 'var(--mute)';

const FILING_STATUSES = [
  { label: 'Single',                      value: 'single' },
  { label: 'Married Filing Jointly',       value: 'married filing jointly' },
  { label: 'Married Filing Separately',    value: 'married filing separately' },
  { label: 'Head of Household',            value: 'head of household' },
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

function StickyHeader({ navigate }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 10,
    }}>
      <span
        onClick={() => navigate('/settings')}
        style={{ fontSize: 18, color: TEXT2, cursor: 'pointer', lineHeight: 1, paddingRight: 4, userSelect: 'none' }}
      >
        ←
      </span>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function FocusInput({ value, onChange, placeholder, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: TEXT1,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  );
}

function FocusSelect({ value, onChange, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: value ? TEXT1 : TEXT3,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        cursor: 'pointer',
      }}
    >
      {children}
    </select>
  );
}

function Field({ label, optional, helper, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: TEXT1 }}>{label}</span>
        {optional && <span style={{ fontSize: 10, color: TEXT3 }}>Optional</span>}
      </div>
      {children}
      {helper && (
        <p style={{ margin: '3px 0 0', fontSize: 10, color: TEXT3, lineHeight: 1.5 }}>
          {helper}
        </p>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--signal-soft)', borderTopColor: BLUE, animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function TaxEdit() {
  const navigate = useNavigate();
  const [annualIncome, setAnnualIncome]         = useState('');
  const [businessExpenses, setBusinessExpenses] = useState('');
  const [filingStatus, setFilingStatus]         = useState('');
  const [state, setState]                       = useState('');
  const [priorYearTax, setPriorYearTax]         = useState('');
  const [error, setError]                       = useState('');
  const [success, setSuccess]                   = useState(false);
  const [loading, setLoading]                   = useState(false);
  const [fetching, setFetching]                 = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/tax/setup')
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        if (d.annual_income    != null) setAnnualIncome(String(d.annual_income));
        if (d.business_expenses != null) setBusinessExpenses(String(d.business_expenses));
        else setBusinessExpenses('0');
        if (d.filing_status)   setFilingStatus(d.filing_status);
        if (d.state)           setState(d.state);
        if (d.prior_year_tax   != null) setPriorYearTax(String(d.prior_year_tax));
      })
      .catch(() => {})
      .finally(() => setFetching(false));
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!annualIncome || !businessExpenses || !filingStatus || !state) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/tax/setup', {
        annual_income:     parseFloat(annualIncome),
        business_expenses: parseFloat(businessExpenses),
        filing_status:     filingStatus,
        state,
        prior_year_tax:    priorYearTax ? parseFloat(priorYearTax) : null,
      });
      posthog.capture('settings_edited');
      setSuccess(true);
      setTimeout(() => navigate('/settings'), 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (fetching) {
    return (
      <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
        <StickyHeader navigate={navigate} />
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
      <StickyHeader navigate={navigate} />

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
        <h1 style={{ margin: '0 0 20px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>
          Edit tax setup
        </h1>

        <form onSubmit={handleSubmit} noValidate>
          <Field label="Gross annual income" helper="Your best estimate for total 1099 income this year">
            <FocusInput
              type="number"
              value={annualIncome}
              onChange={(e) => setAnnualIncome(e.target.value)}
              placeholder="e.g. 80000"
            />
          </Field>

          <Field label="Annual business expenses" helper="Tools, fuel, insurance, phone — anything to do your job">
            <FocusInput
              type="number"
              value={businessExpenses}
              onChange={(e) => setBusinessExpenses(e.target.value)}
              placeholder="e.g. 12000"
            />
          </Field>

          <Field label="Filing status">
            <FocusSelect value={filingStatus} onChange={(e) => setFilingStatus(e.target.value)}>
              <option value="" disabled>Select filing status…</option>
              {FILING_STATUSES.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </FocusSelect>
          </Field>

          <Field label="State">
            <FocusSelect value={state} onChange={(e) => setState(e.target.value)}>
              <option value="" disabled>Select your state…</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </FocusSelect>
          </Field>

          <Field
            label="Last year's total tax paid"
            optional
            helper="We'll factor it into your reserve target so a strong year doesn't catch you short"
          >
            <FocusInput
              type="number"
              value={priorYearTax}
              onChange={(e) => setPriorYearTax(e.target.value)}
              placeholder="e.g. 18000"
            />
          </Field>

          {error && (
            <p style={{
              color: 'var(--stop)', fontSize: 13,
              margin: '0 0 12px', padding: '10px 12px',
              backgroundColor: 'var(--stop-soft)', border: '1px solid var(--stop-border)',
              borderRadius: 8,
            }}>
              {error}
            </p>
          )}

          {success && (
            <p style={{
              color: GREEN, fontSize: 13,
              margin: '0 0 12px', padding: '10px 12px',
              backgroundColor: 'var(--go-soft)', border: '0.5px solid var(--go-border)',
              borderRadius: 8,
            }}>
              Saved successfully
            </p>
          )}

          <button
            type="submit"
            disabled={loading || success}
            style={{
              width: '100%',
              backgroundColor: success ? 'rgba(24,95,165,0.6)' : loading ? 'rgba(24,95,165,0.6)' : BLUE,
              color: 'var(--on-signal)', borderRadius: 10,
              padding: 13, fontSize: 14, fontWeight: 500,
              border: 'none', cursor: (loading || success) ? 'default' : 'pointer',
              marginTop: 8,
            }}
          >
            {loading ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </div>
    </div>
  );
}
